import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getEnv, getTokens, setEnv, setTokens, type EnvName, type Tokens } from './api';

export interface Identity {
  provider: 'email' | 'apple' | 'google';
  email: string | null;
  linked_at: string;
}

/** `PtMe` (change 10). */
export interface Me {
  id: string;
  email: string;
  name: string;
  display_code: string;
  vip_tier: string;
  status: 'active' | 'banned' | 'pending_deletion';
  features: Record<string, boolean | string>;
  created_at: string;
  avatar_url: string | null;
  avatar_key: string | null;
  email_verified: boolean;
  has_password: boolean;
  identities: Identity[];
  deletion: { requested_at: string; purge_after: string } | null;
  is_new?: boolean;
}

export type AuthResult = { user: Me; tokens: Tokens };

const DEVICE_KEY = 'pt.console.device_id';
/** Stable per-browser device id (change 10 device registry / per-device sessions). */
export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

interface SessionState {
  env: EnvName;
  switchEnv: (env: EnvName) => void;
  me: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<Me>;
  register: (email: string, password: string, name: string) => Promise<Me>;
  otpLogin: (email: string, code: string) => Promise<Me>;
  oauthLogin: (provider: 'apple' | 'google', idToken: string, name?: string) => Promise<Me>;
  /** Adopt tokens + user returned by any sign-in-shaped endpoint (password reset, cancel deletion). */
  applyAuthResult: (data: AuthResult) => void;
  logout: (all?: boolean) => Promise<void>;
  reloadMe: () => Promise<void>;
  isAdmin: boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [env, setEnvState] = useState<EnvName>(getEnv());
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const reloadMe = useCallback(async () => {
    if (!getTokens()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api<Me>('/api/me');
      setMe(data);
    } catch {
      setTokens(null);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadMe();
  }, [reloadMe, env]);

  const switchEnv = useCallback((next: EnvName) => {
    setEnv(next);
    setEnvState(next);
    setLoading(true);
  }, []);

  const applyAuthResult = useCallback((data: AuthResult) => {
    setTokens(data.tokens);
    setMe(data.user);
  }, []);

  const signIn = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      const { data } = await api<AuthResult>(path, { method: 'POST', body: { ...body, device_id: getDeviceId() }, auth: false });
      applyAuthResult(data);
      return data.user;
    },
    [applyAuthResult],
  );

  const login = useCallback((email: string, password: string) => signIn('/api/auth/login', { email, password }), [signIn]);
  const register = useCallback((email: string, password: string, name: string) => signIn('/api/auth/register', { email, password, name }), [signIn]);
  const otpLogin = useCallback((email: string, code: string) => signIn('/api/auth/otp/verify', { email, code }), [signIn]);
  const oauthLogin = useCallback((provider: 'apple' | 'google', idToken: string, name?: string) => signIn(`/api/auth/oauth/${provider}`, { id_token: idToken, ...(name ? { name } : {}) }), [signIn]);

  const logout = useCallback(async (all = false) => {
    try {
      await api('/api/auth/logout', { method: 'POST', body: { all } });
    } finally {
      setTokens(null);
      setMe(null);
    }
  }, []);

  const value = useMemo<SessionState>(
    () => ({ env, switchEnv, me, loading, login, register, otpLogin, oauthLogin, applyAuthResult, logout, reloadMe, isAdmin: Boolean(me?.features.admin_role) }),
    [env, switchEnv, me, loading, login, register, otpLogin, oauthLogin, applyAuthResult, logout, reloadMe],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside provider');
  return ctx;
}
